import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import { ErrorState } from '../../components/ui/States';
import {
  CreditReason,
  createCreditReason,
  deleteCreditReason,
  listCreditReasons,
  updateCreditReason,
} from '../../lib/creditReasonApi';

/**
 * Settings -> Credit note reasons (D44).
 *
 * The list every client credit note and vendor return picks its reason from.
 * Admins add and rename reasons here. Built-in reasons and reasons already used
 * on a note can only be hidden: hiding stops a reason being offered for a new
 * choice, and a note already carrying it keeps it. "Other" is never hidden.
 *
 * Self-contained so SettingsPage only has to mount it; `reloadSignal` is bumped by
 * the page's Refresh button.
 */
export const CreditReasonsSettings: React.FC<{ reloadSignal?: number }> = ({ reloadSignal = 0 }) => {
  const [reasons, setReasons] = useState<CreditReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingLabel, setEditingLabel] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReasons(await listCreditReasons({ includeHidden: true }));
    } catch (e: any) {
      setError(e?.message || 'Could not load the reason list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadSignal]);

  const run = async (key: string, action: () => Promise<unknown>) => {
    setBusyKey(key);
    setError(null);
    try {
      await action();
      await load();
      return true;
    } catch (e: any) {
      setError(e?.message || 'That change could not be saved');
      return false;
    } finally {
      setBusyKey(null);
    }
  };

  const onAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    if (await run('__new__', () => createCreditReason({ label }))) setNewLabel('');
  };

  const onRename = async (key: string) => {
    const label = editingLabel.trim();
    if (!label) return;
    if (await run(key, () => updateCreditReason(key, { label }))) setEditingKey(null);
  };

  return (
    <div className="p-6 flex-1 overflow-auto">
      <div className="max-w-2xl">
        <div className="text-sm font-semibold text-slate-800">Credit note reasons</div>
        <p className="text-xs text-slate-500 mt-1 mb-4">
          The reasons a client credit note or a vendor return can be given for. A credit note&apos;s
          reason carries over to the vendor return raised from it. Built-in reasons, and any reason
          already used, can be hidden but not removed; a hidden reason stays on the notes that use it.
        </p>

        {error && (
          <div className="mb-4">
            <ErrorState message={error} onRetry={load} />
          </div>
        )}

        <div className="flex gap-2 mb-5">
          <input
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onAdd();
            }}
            placeholder="New reason, e.g. Leaking packaging"
            aria-label="New reason"
            disabled={busyKey !== null}
            className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
          />
          <Button variant="primary" onClick={onAdd} disabled={!newLabel.trim()} loading={busyKey === '__new__'}>
            Add
          </Button>
        </div>

        {loading && !reasons.length ? (
          <div className="text-sm text-slate-500">Loading reasons...</div>
        ) : (
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {reasons.map((reason) => (
              <div key={reason.key} className="flex items-center gap-3 px-4 py-2.5">
                {editingKey === reason.key ? (
                  <>
                    <input
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onRename(reason.key);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      aria-label={`New name for ${reason.label}`}
                      autoFocus
                      className="flex-1 px-2 py-1 border border-slate-300 rounded-md text-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
                    />
                    <Button size="sm" onClick={() => onRename(reason.key)} disabled={busyKey !== null || !editingLabel.trim()}>
                      Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingKey(null)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <span className={`flex-1 text-sm ${reason.hidden ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                      {reason.label}
                    </span>
                    {reason.noteRequired && <span className="text-xs text-slate-400">note required</span>}
                    {reason.hidden && (
                      <span className="text-xs text-slate-500 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200">
                        hidden
                      </span>
                    )}
                    {reason.builtIn && (
                      <span className="text-xs text-slate-400 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200">
                        built in
                      </span>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busyKey !== null}
                      onClick={() => {
                        setEditingKey(reason.key);
                        setEditingLabel(reason.label);
                      }}
                    >
                      Rename
                    </Button>
                    {!reason.noteRequired && (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busyKey !== null}
                        loading={busyKey === reason.key}
                        onClick={() => run(reason.key, () => updateCreditReason(reason.key, { hidden: !reason.hidden }))}
                      >
                        {reason.hidden ? 'Show' : 'Hide'}
                      </Button>
                    )}
                    {!reason.builtIn && (
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={busyKey !== null}
                        onClick={() => run(reason.key, () => deleteCreditReason(reason.key))}
                      >
                        Remove
                      </Button>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditReasonsSettings;
